import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2, X, ArrowRight } from "lucide-react";
import { formatINR } from "../../data/content";
import { getProductImage } from "../../data/images";
import { POLICIES, NON_AUTHORITATIVE_NOTE } from "../../data/policies";
import { ImageSlot } from "../ImageSlot";
import { useCart } from "../../context/CartContext";
import { CART } from "../../constants/testIds/shop";

const LineItem = ({ line }) => {
  const { setQty, removeItem } = useCart();
  const img = getProductImage(line.image, line.name);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="group flex gap-4 py-6"
      data-testid={CART.item}
    >
      <div className="w-24 shrink-0">
        <div className="relative overflow-hidden border border-brand-sand bg-brand-champagne transition-colors duration-500 group-hover:border-brand-gold/60">
          <ImageSlot name={`cart-${line.key}`} label={line.name} src={img.src} alt={img.alt} ratio="aspect-[4/5]" />
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Link
              to={`/product/${line.slug}`}
              className="font-serif text-base leading-snug text-brand-charcoal transition-colors duration-300 hover:text-brand-gold"
            >
              {line.name}
            </Link>
            {line.variantLabels?.length > 0 && (
              <p className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-brand-stone">
                {line.variantLabels.map((v) => `${v.label}: ${v.optionLabel}`).join(" · ")}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeItem(line.key)}
            aria-label={`Remove ${line.name}`}
            data-testid={CART.itemRemove}
            className="flex h-8 w-8 shrink-0 items-center justify-center text-brand-stone/70 transition-colors duration-300 hover:text-brand-gold"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-auto flex items-center justify-between pt-4">
          <div className="flex items-center border border-brand-sand bg-brand-surface transition-colors duration-300 group-hover:border-brand-gold/50">
            <button
              type="button"
              onClick={() => setQty(line.key, line.qty - 1)}
              aria-label="Decrease quantity"
              data-testid={CART.itemDec}
              className="flex h-9 w-9 items-center justify-center text-brand-charcoal transition-colors duration-300 hover:text-brand-gold"
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-9 text-center text-sm font-semibold tabular-nums text-brand-charcoal" data-testid={CART.itemQty}>
              {line.qty}
            </span>
            <button
              type="button"
              onClick={() => setQty(line.key, line.qty + 1)}
              disabled={line.qty >= (line.maxStock || 99)}
              aria-label="Increase quantity"
              data-testid={CART.itemInc}
              className="flex h-9 w-9 items-center justify-center text-brand-charcoal transition-colors duration-300 hover:text-brand-gold disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <span className="font-serif text-base font-medium tabular-nums text-brand-charcoal">
            {formatINR(line.unitPrice * line.qty)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export const CartDrawer = () => {
  const { items, subtotal, itemCount, isOpen, closeCart, clear } = useCart();
  const navigate = useNavigate();

  // Preview-only figures — clearly labelled non-authoritative.
  const previewDiscount = Math.round((subtotal * POLICIES.firstOrderDiscount) / 100);
  const previewTotal = Math.max(subtotal - previewDiscount, 0);
  const belowMin = subtotal > 0 && subtotal < POLICIES.minOrderValue;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[60] bg-brand-charcoal/50 backdrop-blur-[2px]"
          onClick={closeCart}
          data-testid="cart-overlay"
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-brand-cream shadow-[0_0_80px_-15px_rgba(28,25,23,0.55)]"
            data-testid={CART.drawer}
          >
            {/* Header */}
            <div className="relative flex items-center justify-between border-b border-brand-sand bg-brand-surface px-5 py-5 sm:px-7">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2.5">
                  <span className="h-px w-6 bg-brand-gold/70" aria-hidden="true" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-gold">
                    Your Bracelet Bag
                  </span>
                </div>
                <p className="font-serif text-xl text-brand-charcoal">
                  {itemCount} {itemCount === 1 ? "piece" : "pieces"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeCart}
                aria-label="Close bag"
                data-testid={CART.close}
                className="flex h-10 w-10 items-center justify-center border border-brand-sand text-brand-charcoal transition-colors duration-300 hover:border-brand-gold hover:text-brand-gold"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {items.length === 0 ? (
              <div
                className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center"
                data-testid={CART.empty}
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-brand-gold/30 bg-brand-surface text-brand-gold">
                  <ShoppingBag className="h-8 w-8" strokeWidth={1.4} />
                </span>
                <p className="font-serif text-3xl text-brand-charcoal">Your bag is empty</p>
                <p className="max-w-xs text-sm leading-relaxed text-brand-stone">
                  Handcrafted bracelets, forged in small batches. Find the piece that carries your aura.
                </p>
                <Link
                  to="/shop"
                  onClick={closeCart}
                  className="btn-shimmer group mt-3 inline-flex items-center gap-3 bg-brand-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-brand-cream shadow-[0_12px_30px_-12px_rgba(176,148,96,0.6)] transition-colors duration-300 hover:bg-brand-gold-hover"
                >
                  Explore the Shop
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 sm:px-7">
                  <p className="flex items-center gap-2 pt-5 font-serif text-sm italic text-brand-gold">
                    <span className="h-1 w-1 rounded-full bg-brand-gold" aria-hidden="true" />
                    A little sparkle is on its way.
                  </p>
                  <div className="divide-y divide-brand-sand/80">
                    <AnimatePresence initial={false}>
                      {items.map((line) => (
                        <LineItem key={line.key} line={line} />
                      ))}
                    </AnimatePresence>
                  </div>
                  <div className="py-5">
                    <button
                      type="button"
                      onClick={clear}
                      className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-stone transition-colors duration-300 hover:text-brand-gold"
                    >
                      Clear bag
                    </button>
                  </div>
                </div>

                {/* Summary */}
                <div className="border-t border-brand-sand bg-brand-surface px-5 py-6 sm:px-7">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-stone">Subtotal</span>
                    <span className="font-serif text-2xl font-medium tabular-nums text-brand-charcoal" data-testid={CART.subtotal}>
                      {formatINR(subtotal)}
                    </span>
                  </div>

                  {/* Non-authoritative preview summary */}
                  <div className="mt-4 space-y-2 border-t border-dashed border-brand-sand pt-4 text-xs">
                    <div className="flex items-center justify-between text-brand-stone">
                      <span>First-order discount ({POLICIES.firstOrderDiscount}%) — preview</span>
                      <span className="tabular-nums text-brand-gold">-{formatINR(previewDiscount)}</span>
                    </div>
                    <div className="flex items-center justify-between text-brand-stone">
                      <span>Shipping</span>
                      <span>Calculated at checkout</span>
                    </div>
                    <div className="flex items-center justify-between text-brand-stone">
                      <span>Wallet credit (up to {POLICIES.walletMaxUsage}%)</span>
                      <span>Applied at checkout</span>
                    </div>
                    <div className="mt-1 flex items-baseline justify-between border-t border-brand-sand/70 pt-3 text-brand-charcoal">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">Estimated total</span>
                      <span className="font-serif text-lg font-medium tabular-nums">{formatINR(previewTotal)}</span>
                    </div>
                  </div>

                  {belowMin && (
                    <p className="mt-4 border-l-2 border-brand-gold bg-brand-gold/10 px-3 py-2 text-[11px] text-brand-charcoal">
                      Minimum order value of {formatINR(POLICIES.minOrderValue)} applies at checkout.
                    </p>
                  )}

                  <p className="mt-4 text-[10px] leading-relaxed text-brand-stone/80">{NON_AUTHORITATIVE_NOTE}</p>

                  <button
                    type="button"
                    onClick={() => {
                      closeCart();
                      navigate("/checkout");
                    }}
                    data-testid={CART.checkout}
                    className="btn-shimmer group mt-5 flex w-full items-center justify-center gap-3 bg-brand-gold py-4 text-sm font-semibold uppercase tracking-[0.18em] text-brand-cream shadow-[0_16px_34px_-14px_rgba(176,148,96,0.65)] transition-colors duration-300 hover:bg-brand-gold-hover"
                  >
                    Proceed to Checkout
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
